import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Trash2, Edit2, Search, Wrench, DollarSign, Calendar, 
  Hash, ImagePlus, X, AlertCircle, CheckCircle, Clock, XCircle,
  Filter, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'Working', label: 'Working', color: 'bg-green-500', icon: CheckCircle },
  { value: 'Out of Service', label: 'Out of Service', color: 'bg-red-500', icon: XCircle },
  { value: 'Out for Repair', label: 'Out for Repair', color: 'bg-amber-500', icon: Clock },
  { value: 'Retired', label: 'Retired', color: 'bg-gray-500', icon: AlertCircle }
];

const CONDITION_OPTIONS = ['Excellent', 'Good', 'Fair', 'Poor'];

const DEFAULT_CATEGORIES = [
  'Speakers', 'Subwoofers', 'Lighting', 'Mixers', 'Microphones', 
  'Cables', 'Stands', 'Controllers', 'Effects', 'Accessories', 'Cases', 'Other'
];

export default function EquipmentPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Speakers',
    serial_number: '',
    purchase_date: '',
    purchased_from: '',
    cost: '',
    condition: 'Good',
    status: 'Working',
    image: '',
    notes: '',
    warranty_expiry: '',
    last_service_date: ''
  });

  useEffect(() => {
    fetchEquipment();
    fetchCategories();
  }, []);

  const fetchEquipment = async () => {
    try {
      const response = await axios.get(`${API}/equipment`);
      setEquipment(response.data);
    } catch (error) {
      toast.error('Failed to fetch equipment');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/equipment/categories/list`);
      setCategories(response.data);
    } catch (error) {
      // Use defaults if fetch fails
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Equipment name is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        cost: formData.cost ? parseFloat(formData.cost) : null
      };

      if (editingEquipment) {
        await axios.put(`${API}/equipment/${editingEquipment.id}`, payload);
        toast.success('Equipment updated successfully');
      } else {
        await axios.post(`${API}/equipment`, payload);
        toast.success('Equipment added successfully');
      }
      
      fetchEquipment();
      handleCloseDialog();
    } catch (error) {
      toast.error('Failed to save equipment');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/equipment/${id}`);
      toast.success('Equipment deleted');
      fetchEquipment();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error('Failed to delete equipment');
    }
  };

  const handleEdit = (item) => {
    setEditingEquipment(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      category: item.category || 'Speakers',
      serial_number: item.serial_number || '',
      purchase_date: item.purchase_date || '',
      purchased_from: item.purchased_from || '',
      cost: item.cost?.toString() || '',
      condition: item.condition || 'Good',
      status: item.status || 'Working',
      image: item.image || '',
      notes: item.notes || '',
      warranty_expiry: item.warranty_expiry || '',
      last_service_date: item.last_service_date || ''
    });
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingEquipment(null);
    setFormData({
      name: '',
      description: '',
      category: 'Speakers',
      serial_number: '',
      purchase_date: '',
      purchased_from: '',
      cost: '',
      condition: 'Good',
      status: 'Working',
      image: '',
      notes: '',
      warranty_expiry: '',
      last_service_date: ''
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image: response.data.url });
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  // Filter and sort equipment
  const filteredEquipment = equipment
    .filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.serial_number?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
      const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
      return matchesSearch && matchesCategory && matchesStatus;
    })
    .sort((a, b) => {
      let aVal = a[sortBy] || '';
      let bVal = b[sortBy] || '';
      if (sortBy === 'cost') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  // Calculate summary stats
  const stats = {
    total: equipment.length,
    working: equipment.filter(e => e.status === 'Working').length,
    outOfService: equipment.filter(e => e.status === 'Out of Service').length,
    outForRepair: equipment.filter(e => e.status === 'Out for Repair').length,
    totalValue: equipment.reduce((sum, e) => sum + (e.cost || 0), 0)
  };

  const getStatusBadge = (status) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    const Icon = statusOption.icon;
    return (
      <Badge className={`${statusOption.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {statusOption.label}
      </Badge>
    );
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'Excellent': return 'text-green-400';
      case 'Good': return 'text-blue-400';
      case 'Fair': return 'text-amber-400';
      case 'Poor': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-neon-blue"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading text-3xl text-white flex items-center gap-3">
              <Wrench className="w-8 h-8 text-neon-blue" />
              Equipment
            </h1>
            <p className="text-muted-foreground mt-1">Manage your owned equipment and inventory</p>
          </div>
          <Button 
            onClick={() => setShowDialog(true)}
            className="bg-neon-blue text-black hover:bg-neon-blue/80"
            data-testid="add-equipment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Equipment
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="glass-card border-glass-border">
            <CardContent className="p-4 text-center">
              <p className="text-muted-foreground text-sm">Total Items</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-glass-border">
            <CardContent className="p-4 text-center">
              <p className="text-muted-foreground text-sm">Working</p>
              <p className="text-2xl font-bold text-green-400">{stats.working}</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-glass-border">
            <CardContent className="p-4 text-center">
              <p className="text-muted-foreground text-sm">Out for Repair</p>
              <p className="text-2xl font-bold text-amber-400">{stats.outForRepair}</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-glass-border">
            <CardContent className="p-4 text-center">
              <p className="text-muted-foreground text-sm">Out of Service</p>
              <p className="text-2xl font-bold text-red-400">{stats.outOfService}</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-glass-border">
            <CardContent className="p-4 text-center">
              <p className="text-muted-foreground text-sm">Total Value</p>
              <p className="text-2xl font-bold text-neon-blue">
                ${stats.totalValue.toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/10 border-glass-border"
              data-testid="equipment-search"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-48 bg-white/10 border-glass-border" data-testid="filter-category">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-48 bg-white/10 border-glass-border" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUS_OPTIONS.map(status => (
                <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-40 bg-white/10 border-glass-border">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="category">Category</SelectItem>
              <SelectItem value="cost">Cost</SelectItem>
              <SelectItem value="purchase_date">Purchase Date</SelectItem>
              <SelectItem value="status">Status</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Equipment Grid */}
        {filteredEquipment.length === 0 ? (
          <Card className="glass-card border-glass-border">
            <CardContent className="p-12 text-center">
              <Wrench className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No equipment found</h3>
              <p className="text-muted-foreground mb-4">
                {equipment.length === 0 
                  ? "Add your first piece of equipment to get started"
                  : "Try adjusting your filters"}
              </p>
              {equipment.length === 0 && (
                <Button onClick={() => setShowDialog(true)} className="bg-neon-blue text-black">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Equipment
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEquipment.map(item => (
              <Card 
                key={item.id} 
                className="glass-card border-glass-border hover:border-neon-blue/50 transition-all"
                data-testid={`equipment-card-${item.id}`}
              >
                <CardContent className="p-0">
                  {/* Image Section */}
                  {item.image ? (
                    <div className="h-40 overflow-hidden rounded-t-lg">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-40 bg-white/5 rounded-t-lg flex items-center justify-center">
                      <Wrench className="w-12 h-12 text-white/20" />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-white text-lg">{item.name}</h3>
                        <p className="text-muted-foreground text-sm">{item.category}</p>
                      </div>
                      {getStatusBadge(item.status)}
                    </div>
                    
                    {item.description && (
                      <p className="text-sm text-white/70 line-clamp-2">{item.description}</p>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {item.serial_number && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Hash className="w-3 h-3" />
                          <span className="truncate">{item.serial_number}</span>
                        </div>
                      )}
                      {item.cost && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <DollarSign className="w-3 h-3" />
                          <span>${item.cost.toLocaleString()}</span>
                        </div>
                      )}
                      {item.purchase_date && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>{item.purchase_date}</span>
                        </div>
                      )}
                      {item.condition && (
                        <div className={`flex items-center gap-1 ${getConditionColor(item.condition)}`}>
                          <span>{item.condition}</span>
                        </div>
                      )}
                    </div>
                    
                    {item.purchased_from && (
                      <p className="text-xs text-muted-foreground">
                        From: {item.purchased_from}
                      </p>
                    )}
                    
                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t border-glass-border">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleEdit(item)}
                        className="flex-1 text-white/70 hover:text-white hover:bg-white/10"
                        data-testid={`edit-equipment-${item.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setDeleteConfirm(item)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-equipment-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="glass-card border-glass-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="equipment-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Wrench className="w-5 h-5 text-neon-blue" />
                {editingEquipment ? 'Edit Equipment' : 'Add Equipment'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              {/* Basic Info */}
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., JBL EON 615"
                  className="bg-white/10 border-glass-border"
                  data-testid="equipment-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="bg-white/10 border-glass-border" data-testid="equipment-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the equipment"
                  className="bg-white/10 border-glass-border resize-none"
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input
                  value={formData.serial_number}
                  onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                  placeholder="Serial number"
                  className="bg-white/10 border-glass-border"
                  data-testid="equipment-serial-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Cost ($)</Label>
                <Input
                  type="number"
                  value={formData.cost}
                  onChange={(e) => setFormData({...formData, cost: e.target.value})}
                  placeholder="0.00"
                  className="bg-white/10 border-glass-border"
                  data-testid="equipment-cost-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Purchase Date</Label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                  className="bg-white/10 border-glass-border"
                  data-testid="equipment-purchase-date-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Purchased From</Label>
                <Input
                  value={formData.purchased_from}
                  onChange={(e) => setFormData({...formData, purchased_from: e.target.value})}
                  placeholder="Store or seller name"
                  className="bg-white/10 border-glass-border"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Condition</Label>
                <Select value={formData.condition} onValueChange={(v) => setFormData({...formData, condition: v})}>
                  <SelectTrigger className="bg-white/10 border-glass-border" data-testid="equipment-condition-select">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITION_OPTIONS.map(cond => (
                      <SelectItem key={cond} value={cond}>{cond}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger className="bg-white/10 border-glass-border" data-testid="equipment-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Warranty Expiry</Label>
                <Input
                  type="date"
                  value={formData.warranty_expiry}
                  onChange={(e) => setFormData({...formData, warranty_expiry: e.target.value})}
                  className="bg-white/10 border-glass-border"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Last Service Date</Label>
                <Input
                  type="date"
                  value={formData.last_service_date}
                  onChange={(e) => setFormData({...formData, last_service_date: e.target.value})}
                  className="bg-white/10 border-glass-border"
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes about this equipment"
                  className="bg-white/10 border-glass-border resize-none"
                  rows={2}
                />
              </div>
              
              {/* Image Upload */}
              <div className="space-y-2 md:col-span-2">
                <Label>Image</Label>
                <div className="flex items-center gap-4">
                  {formData.image ? (
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden">
                      <img src={formData.image} alt="Equipment" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setFormData({...formData, image: ''})}
                        className="absolute top-1 right-1 p-1 bg-black/70 rounded-full hover:bg-black"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ) : (
                    <label className="w-24 h-24 rounded-lg border-2 border-dashed border-glass-border flex items-center justify-center cursor-pointer hover:border-neon-blue/50 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <ImagePlus className="w-8 h-8 text-muted-foreground" />
                    </label>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Upload a photo of the equipment
                  </p>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog} className="border-glass-border">
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="bg-neon-blue text-black hover:bg-neon-blue/80"
                data-testid="save-equipment-btn"
              >
                {editingEquipment ? 'Save Changes' : 'Add Equipment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="glass-card border-glass-border max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                Delete Equipment
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Are you sure you want to delete <span className="text-white font-medium">{deleteConfirm?.name}</span>? 
              This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-glass-border">
                Cancel
              </Button>
              <Button 
                onClick={() => handleDelete(deleteConfirm.id)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
